/**
 * Camera capture for the attachment sheet.
 *
 * The `capture` attribute on a file input only does anything on mobile; on
 * desktop it degrades to an ordinary file picker, so "Camera" and "Photo" would
 * be the same button. This opens the webcam via `getUserMedia`, previews the
 * frame, and hands back a JPEG `File`.
 *
 * The stream is stopped on every exit path — closing, capturing, retaking, or
 * unmounting. A missed stop leaves the camera light on.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Camera, X, RotateCcw, Check, SwitchCamera } from 'lucide-react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

interface CameraCaptureDialogProps {
  open: boolean
  onClose: () => void
  onCapture: (file: File) => void
}

export function CameraCaptureDialog({ open, onClose, onCapture }: CameraCaptureDialogProps) {
  const t = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [shot, setShot] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [facing, setFacing] = useState<'user' | 'environment'>('user')

  const mirrored = facing === 'user'

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const startStream = useCallback(async () => {
    setError(null)
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(t('presence.cameraUnsupported'))
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => {})
      }
    } catch (err) {
      const name = (err as { name?: string })?.name
      setError(
        name === 'NotAllowedError'
          ? t('presence.cameraDenied')
          : name === 'NotFoundError'
            ? t('presence.cameraNotFound')
            : t('presence.cameraFailed'),
      )
    }
  }, [facing, t])

  useEffect(() => {
    if (!open) {
      stopStream()
      setShot(null)
      return
    }
    if (!shot) void startStream()
    return stopStream
  }, [open, shot, startStream, stopStream])

  const capture = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    if (mirrored) {
      ctx.translate(canvas.width, 0)
      ctx.scale(-1, 1)
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    setShot(canvas.toDataURL('image/jpeg', 0.9))
    stopStream()
  }

  const confirm = () => {
    if (!shot) return
    const [, base64] = shot.split(',')
    if (!base64) return
    const bytes = atob(base64)
    const buf = new Uint8Array(bytes.length)
    for (let i = 0; i < bytes.length; i += 1) buf[i] = bytes.charCodeAt(i)
    onCapture(new File([buf], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }))
    setShot(null)
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Camera className="h-4 w-4" />
            {t('presence.camera')}
          </span>
          <button
            onClick={() => { stopStream(); setShot(null); onClose() }}
            className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative aspect-[4/3] bg-black">
          {error ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
              {error}
            </div>
          ) : shot ? (
            <img src={shot} alt="" className="h-full w-full object-contain" />
          ) : (
            <video
              ref={videoRef}
              playsInline
              muted
              className="h-full w-full object-cover"
              style={{ transform: mirrored ? 'scaleX(-1)' : undefined }}
            />
          )}
        </div>

        <div className="flex items-center justify-center gap-4 p-4">
          {shot ? (
            <>
              <button
                onClick={() => setShot(null)}
                className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
              >
                <RotateCcw className="h-4 w-4" />
                {t('presence.retake')}
              </button>
              <button
                onClick={confirm}
                className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform active:scale-95"
              >
                <Check className="h-4 w-4" />
                {t('presence.usePhoto')}
              </button>
            </>
          ) : (
            !error && (
              <>
                <button
                  onClick={() => setFacing((f) => (f === 'user' ? 'environment' : 'user'))}
                  className="rounded-full p-3 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t('presence.switchCamera')}
                >
                  <SwitchCamera className="h-5 w-5" />
                </button>
                <button
                  onClick={capture}
                  className="h-14 w-14 rounded-full border-4 border-primary/30 bg-primary transition-transform active:scale-95"
                  aria-label={t('presence.capture')}
                />
              </>
            )
          )}
        </div>
      </div>
    </div>
  )
}
